import React, { useMemo, useCallback } from "react";
import { ExternalLinkIcon } from "lucide-react";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { getRandomStatusText } from "./utils";

interface ApplyFileEditsBlockProps {
  args: any;
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  toolResult?: any;
  toolCallId?: string;
  messageId: string;
}

export const ApplyFileEditsBlock: React.FC<ApplyFileEditsBlockProps> =
  React.memo(({ args, status, toolResult, toolCallId, messageId }) => {
    const openCodeDiffPanel = useAiContextStore(
      (state) => state.openCodeDiffPanel
    );
    const setNewLayout = useLayoutStore((store) => store.setNewLayout);

    const { hasDiffData, fileEdited } = useMemo(() => {
      const hasDiffData =
        toolResult?.oldContents !== undefined &&
        (args?.filename || toolResult?.filePath);
      const fileEdited = args?.filename || toolResult?.filePath || "";

      return { hasDiffData, fileEdited };
    }, [toolResult?.oldContents, args?.filename, toolResult?.filePath]);

    const statusIcon = useMemo(() => {
      switch (status) {
        case "succeeded":
          return (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/success.svg"
                className="size-4 shrink-0 flex"
                alt="Success"
              />
            </div>
          );
        case "failed":
          return (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/failed.svg"
                className="size-4 shrink-0"
                alt="Failed"
              />
            </div>
          );
        case "aborted":
          return (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/failed.svg"
                className="size-4 shrink-0"
                alt="Aborted"
              />
            </div>
          );
        case "in-progress":
        default:
          return (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/progress.svg"
                className="size-4 shrink-0 flex animate-pulse"
                alt="In Progress"
              />
            </div>
          );
      }
    }, [status]);

    const handleDiffClick = useCallback(() => {
      if (toolCallId) {
        openCodeDiffPanel(toolCallId, messageId);
        // Switch to EngSpec tab with a small delay to ensure data is ready
        setTimeout(() => {
          setNewLayout(LeftTab.EngSpec);
        }, 50);
      }
    }, [toolCallId, messageId, openCodeDiffPanel, setNewLayout]);

    return (
      <div className="rounded-md text-xs my-2 p-2 bg-secondary-n-950 text-secondary-n-400 break-all flex flex-col items-start justify-start transition-colors duration-200 group">
        <div className="flex flex-row gap-2 items-center justify-start w-full relative">
          {statusIcon}
          <div className="flex items-center flex-1">
            {hasDiffData && status === "succeeded" ? (
              <span
                className="text-xs cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={handleDiffClick}
              >
                {fileEdited}
              </span>
            ) : (
              <span className="text-xs">{fileEdited}</span>
            )}
            {!fileEdited && (
              <span className="text-xs text-secondary-n-500">
                {getRandomStatusText()}
              </span>
            )}
          </div>
          {hasDiffData && status === "succeeded" && (
            <ExternalLinkIcon
              className="w-4 h-4 text-secondary-n-400 hover:text-secondary-n-200 cursor-pointer transition-colors duration-200 opacity-0 group-hover:opacity-100"
              onClick={handleDiffClick}
            />
          )}
        </div>
      </div>
    );
  });
